<script setup lang="ts">
import {
  ArrowLeft as ArrowLeftIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  MapPin as MapPinIcon,
  Maximize as MaximizeIcon,
  Minimize as MinimizeIcon,
  Link as LinkIcon,
  Loader2 as Loader2Icon,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
  type ComponentPublicInstance,
} from 'vue'
import { useRouter } from 'vue-router'

import { useEventListener } from '@/composables/useEventListener'
import { usePhotoRouteData } from '@/composables/usePhotoRouteData'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { resolvePhotoUrl } from '@/services/offlinePhotoResolver'
import {
  ensureWorkspacePhotoAssetUrlsFresh,
  type PhotoAssetVariant,
} from '@/services/photoAssetUrlService'
import { isTauri, isTauriAppleMobile } from '@/services/platformService'
import { useSafeAreaInsets } from '@/services/safeAreaService'
import { settingsService } from '@/services/settingsService'
import { isNativeFullscreen, setNativeFullscreen } from '@/services/tauriWindowService'
import { showError, showSuccess } from '@/services/toastService'
import { isSmallScreen } from '@/services/uiService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { usePhotoStore } from '@/stores/photoStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Photo } from '@/types/photo'
import { ConcurrencyLimiter } from '@/utils/concurrencyLimiter'

const props = defineProps<{
  isOpen: boolean
  initialPhotoId?: string
}>()

const emit = defineEmits<{
  close: []
  'navigate-to-location': [photoId: string]
}>()

const photoStore = usePhotoStore()
const workspaceStore = useWorkspaceStore()
const router = useRouter()
const { photosSortedByDate: photos } = storeToRefs(photoStore)
const safeAreaInsets = useSafeAreaInsets()
const isTauriApp = isTauri()
const isTauriAppleMobileApp = isTauriAppleMobile()

const currentPhotoIndex = ref(0)
const slideshowRef = ref<HTMLDivElement | null>(null)
const viewportRef = ref<HTMLDivElement | null>(null)
const viewportWidth = ref(1)
const thumbnailsContainerRef = ref<HTMLDivElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
const thumbnailElementRefs = ref<Map<number, HTMLElement>>(new Map())
const slideImageRefs = ref<Map<string, HTMLImageElement>>(new Map())
const isFullscreen = ref(false)
const isImmersiveMobileFullscreen = ref(false)
const controlsVisible = ref(true)
const showOverlays = ref(false)
const autoShowOverlay = ref(
  settingsService.getSetting<boolean>(UI_SETTING_KEYS.photoSlideshowAutoShowOverlay) === true,
)
const unsubscribeAutoShowOverlaySetting = settingsService.subscribe(({ key, value, source }) => {
  if (source === 'remote' || key !== UI_SETTING_KEYS.photoSlideshowAutoShowOverlay) return
  autoShowOverlay.value = value === true
})
let autoHideTimer: ReturnType<typeof setTimeout> | null = null
const hideControlsTimer = ref<number | null>(null)
const cursorVisible = ref(true)
const isSlideTransitioning = ref(false)
let resizeObserver: ResizeObserver | null = null
let observedViewport: HTMLDivElement | null = null
const SLIDE_GAP = 24

// Loading state management
const isCurrentImageLoading = ref(false)
const showSpinner = ref(false)
const spinnerTimer = ref<number | null>(null)

// Safe-area aware overlay positioning
const photoInfoOverlayStyle = computed(() => ({
  bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
  right: 'calc(1rem + env(safe-area-inset-right, 0px))',
}))

const controlBarStyle = computed(() => ({
  paddingTop: `calc(1rem + ${safeAreaInsets.value.top})`,
  paddingLeft: `calc(1rem + ${safeAreaInsets.value.left})`,
  paddingRight: `calc(1rem + ${safeAreaInsets.value.right})`,
  paddingBottom: '1rem',
}))

const thumbnailBarStyle = computed(() => ({
  paddingBottom: `calc(0.75rem + ${safeAreaInsets.value.bottom})`,
  paddingLeft: safeAreaInsets.value.left,
  paddingRight: safeAreaInsets.value.right,
}))

// Touch/swipe gesture handling
const isTouch = ref(false)
const touchStartX = ref(0)
const touchStartY = ref(0)
const touchCurrentX = ref(0)
const touchCurrentY = ref(0)
const touchStartTime = ref(0)
const isDragging = ref(false)
const imageTranslateX = ref(0)
const imageScale = ref(1)
const imagePanX = ref(0)
const imagePanY = ref(0)
const isPinching = ref(false)
const isZoomPanning = ref(false)
const pinchStartDistance = ref(0)
const pinchStartScale = ref(1)
const pinchStartPanX = ref(0)
const pinchStartPanY = ref(0)
const pinchStartCenterX = ref(0)
const pinchStartCenterY = ref(0)
const panStartX = ref(0)
const panStartY = ref(0)
const panOriginX = ref(0)
const panOriginY = ref(0)
const swipeThreshold = 30 // Minimum distance to trigger swipe (reduced for more responsiveness)
const velocityThreshold = 0.3 // Minimum velocity to trigger swipe (reduced for more sensitivity)
const animationId = ref<number | null>(null)
const hasHiddenControlsThisGesture = ref(false)
const lastTouchEndTime = ref(0)
const MIN_IMAGE_SCALE = 1
const MAX_IMAGE_SCALE = 4

// iOS-like resistance function using exponential curve
const calculateResistance = (distance: number, maxDistance = 200): number => {
  const normalizedDistance = Math.abs(distance) / maxDistance
  // Use exponential decay for smooth resistance curve
  return Math.max(0.1, Math.exp(-normalizedDistance * 2))
}

// Blob URL cache to manage created URLs
const blobUrlCache = ref<Map<string, string>>(new Map())
// Preloaded image cache to store Image objects for smooth navigation
const preloadedImageCache = ref<Map<string, HTMLImageElement>>(new Map())
// Thumbnail loading management
const loadedThumbnails = ref(new Set<string>())
const thumbnailObserver = ref<IntersectionObserver | null>(null)
const thumbnailLimiter = new ConcurrencyLimiter(5)
const resolvedSlideUrls = ref<Map<string, string>>(new Map())
const resolvedThumbnailUrls = ref<Map<string, string>>(new Map())

const clearResolvedPhotoAssetCaches = () => {
  resolvedSlideUrls.value = new Map()
  resolvedThumbnailUrls.value = new Map()
  preloadedImageCache.value.clear()
}

const refreshPhotoAssetUrlsIfNeeded = async (
  targetPhotos: Photo[],
  variants: PhotoAssetVariant[],
): Promise<boolean> => {
  if (!workspaceStore.clientId || targetPhotos.length === 0) {
    return false
  }

  const refreshed = await ensureWorkspacePhotoAssetUrlsFresh(
    workspaceStore.clientId,
    targetPhotos,
    {
      variants,
    },
  )
  if (refreshed) {
    clearResolvedPhotoAssetCaches()
  }
  return refreshed
}

const updateViewportWidth = () => {
  if (typeof window === 'undefined') return

  const viewportElementWidth = viewportRef.value?.clientWidth ?? 0
  if (viewportElementWidth > 0) {
    viewportWidth.value = viewportElementWidth
    return
  }

  const slideshowWidth = slideshowRef.value?.clientWidth ?? 0
  if (slideshowWidth > 0) {
    viewportWidth.value = slideshowWidth
    return
  }

  viewportWidth.value = window.innerWidth || viewportWidth.value || 1
}

const getEffectiveViewportWidth = () => {
  const viewportElementWidth = viewportRef.value?.clientWidth ?? 0
  if (viewportElementWidth > 0) {
    return viewportElementWidth
  }

  const slideshowWidth = slideshowRef.value?.clientWidth ?? 0
  if (slideshowWidth > 0) {
    return slideshowWidth
  }

  if (typeof window !== 'undefined') {
    return window.innerWidth || viewportWidth.value || 1
  }

  return viewportWidth.value || 1
}

const currentPhoto = computed<Photo | undefined>(() => {
  if (photos.value.length === 0) return undefined
  return photos.value[currentPhotoIndex.value]
})

// Route data (km on route, elevation gain, gradient, elevation profile, mini map)
const { tours } = storeToRefs(workspaceStore)
const routeData = usePhotoRouteData(currentPhoto, tours)

// Mini map canvas
const miniMapCanvasRef = ref<HTMLCanvasElement | null>(null)

const drawMiniMap = () => {
  const canvas = miniMapCanvasRef.value
  const ctx = canvas?.getContext('2d')
  if (!canvas || !ctx) return

  const { routePolyline, photoPoint } = routeData.value
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  if (!routePolyline || routePolyline.length < 2) return

  const W = canvas.width
  const H = canvas.height
  const PAD = 6

  const lats = routePolyline.map(([, lat]) => lat)
  const lons = routePolyline.map(([lon]) => lon)
  if (photoPoint) {
    lats.push(photoPoint[1])
    lons.push(photoPoint[0])
  }
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLon = Math.min(...lons)
  const maxLon = Math.max(...lons)

  const cosLat = Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180))
  const lonRange = (maxLon - minLon) * cosLat || 1e-6
  const latRange = maxLat - minLat || 1e-6
  const scaleX = (W - PAD * 2) / lonRange
  const scaleY = (H - PAD * 2) / latRange
  const scale = Math.min(scaleX, scaleY)
  const offX = PAD + (W - PAD * 2 - lonRange * scale) / 2
  const offY = PAD + (H - PAD * 2 - latRange * scale) / 2

  const toCanvas = ([lon, lat]: [number, number]): [number, number] => [
    offX + (lon - minLon) * cosLat * scale,
    H - offY - (lat - minLat) * scale,
  ]

  ctx.beginPath()
  ctx.strokeStyle = 'rgba(255,255,255,0.85)'
  ctx.lineWidth = 1.5
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  const [x0, y0] = toCanvas(routePolyline[0])
  ctx.moveTo(x0, y0)
  for (let i = 1; i < routePolyline.length; i++) {
    const [x, y] = toCanvas(routePolyline[i])
    ctx.lineTo(x, y)
  }
  ctx.stroke()

  if (photoPoint) {
    const [px, py] = toCanvas([photoPoint[0], photoPoint[1]])
    ctx.beginPath()
    ctx.arc(px, py, 4, 0, Math.PI * 2)
    ctx.fillStyle = '#f97316'
    ctx.fill()
    ctx.strokeStyle = 'white'
    ctx.lineWidth = 1.5
    ctx.stroke()
  }
}

watch([miniMapCanvasRef, routeData], drawMiniMap, { flush: 'post' })

// Elevation sparkline SVG path
const elevationProfilePath = computed(() => {
  const profile = routeData.value.elevationProfile
  if (!profile || profile.length < 2) return null

  const W = 120
  const H = 28
  const minD = profile[0].d
  const maxD = profile[profile.length - 1].d
  const eles = profile.map((pt) => pt.ele)
  const minEle = Math.min(...eles)
  const maxEle = Math.max(...eles)
  const dRange = maxD - minD || 1
  const eleRange = maxEle - minEle || 1

  const points = profile
    .map((pt) => {
      const x = ((pt.d - minD) / dRange) * W
      const y = H - ((pt.ele - minEle) / eleRange) * H
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return { points, W, H }
})

// Photo position dot on the elevation sparkline
// Marker expressed as percentages so it renders correctly regardless of SVG scaling.
// Clamped to [3%, 97%] so the 10px CSS dot never bleeds past the container edge.
const elevationProfileMarker = computed(() => {
  const profile = routeData.value.elevationProfile
  const km = routeData.value.kmOnRoute
  if (!profile || profile.length < 2 || km === null) return null

  let snap = profile[0]
  let snapDiff = Math.abs(profile[0].d - km)
  for (const pt of profile) {
    const diff = Math.abs(pt.d - km)
    if (diff < snapDiff) {
      snapDiff = diff
      snap = pt
    }
  }

  const minD = profile[0].d
  const maxD = profile[profile.length - 1].d
  const eles = profile.map((pt) => pt.ele)
  const minEle = Math.min(...eles)
  const maxEle = Math.max(...eles)
  const dRange = maxD - minD || 1
  const eleRange = maxEle - minEle || 1

  return {
    x: Math.min(Math.max(3, ((snap.d - minD) / dRange) * 100), 97),
    y: Math.min(Math.max(3, (1 - (snap.ele - minEle) / eleRange) * 100), 97),
  }
})

const selectPhotoById = (photoId: string | null | undefined) => {
  if (!photoId) return false
  const index = photos.value.findIndex((photo) => photo.clientId === photoId)
  if (index === -1) return false

  currentPhotoIndex.value = index
  return true
}

// Get previous and next photo for preloading
const previousPhotoData = computed<Photo | undefined>(() => {
  if (photos.value.length <= 1) return undefined
  if (currentPhotoIndex.value <= 0) return undefined
  return photos.value[currentPhotoIndex.value - 1]
})

const nextPhotoData = computed<Photo | undefined>(() => {
  const total = photos.value.length
  if (total <= 1) return undefined
  if (currentPhotoIndex.value >= total - 1) return undefined
  return photos.value[currentPhotoIndex.value + 1]
})

// Helper function to get URL for any photo
const getPhotoUrl = (photo: Photo): string => {
  if (!photo) return ''

  const resolvedOffline = resolvedSlideUrls.value.get(photo.clientId)
  if (resolvedOffline) {
    return resolvedOffline
  }

  if (photo.binaryData) {
    // Reuse cached blob URLs when available to avoid leaks
    if (photo.clientId && blobUrlCache.value.has(photo.clientId)) {
      return blobUrlCache.value.get(photo.clientId) || ''
    }

    const objectUrl = URL.createObjectURL(photo.binaryData)

    if (photo.clientId) {
      blobUrlCache.value = new Map(blobUrlCache.value).set(photo.clientId, objectUrl)
    }

    return objectUrl
  }

  if (photo._binaryDataUrl) {
    return photo._binaryDataUrl
  }

  return ''
}

const getResolvedSlideUrl = (photo: Photo): string => {
  return (
    photo._resolvedOfflineBinaryUrl ||
    resolvedSlideUrls.value.get(photo.clientId) ||
    getPhotoUrl(photo)
  )
}

const ensureThumbnailUrl = async (photo: Photo): Promise<string | null> => {
  await refreshPhotoAssetUrlsIfNeeded([photo], ['thumb'])

  const cached = resolvedThumbnailUrls.value.get(photo.clientId)
  if (cached) {
    return cached
  }

  const rawUrl = photo._thumbnailData || photo._thumbnailUrl || null
  if (!rawUrl) {
    return null
  }

  const resolvedUrl = await resolvePhotoUrl(
    workspaceStore.clientId,
    photo.clientId,
    rawUrl,
    'thumb',
  )
  resolvedThumbnailUrls.value = new Map(resolvedThumbnailUrls.value).set(
    photo.clientId,
    resolvedUrl,
  )
  photo._resolvedOfflineThumbnailUrl = resolvedUrl
  return resolvedUrl
}

const ensureSlideUrl = async (photo: Photo): Promise<string | null> => {
  await refreshPhotoAssetUrlsIfNeeded([photo], ['full'])

  const cached = resolvedSlideUrls.value.get(photo.clientId)
  if (cached) {
    return cached
  }

  const rawUrl = photo._binaryDataUrl || getPhotoUrl(photo)
  if (!rawUrl) {
    return null
  }

  const resolvedUrl = await resolvePhotoUrl(workspaceStore.clientId, photo.clientId, rawUrl, 'full')
  resolvedSlideUrls.value = new Map(resolvedSlideUrls.value).set(photo.clientId, resolvedUrl)
  photo._resolvedOfflineBinaryUrl = resolvedUrl
  return resolvedUrl
}

const slidePhotos = computed<Photo[]>(() => {
  const activePhoto = currentPhoto.value
  if (!activePhoto) {
    return []
  }

  const total = photos.value.length
  if (total <= 1) {
    return [activePhoto]
  }

  const currentIndex = currentPhotoIndex.value
  const result: Photo[] = []

  if (currentIndex > 0) {
    const prevPhoto = photos.value[currentIndex - 1]
    if (prevPhoto) {
      result.push(prevPhoto)
    }
  }

  result.push(activePhoto)

  if (currentIndex < total - 1) {
    const nextPhoto = photos.value[currentIndex + 1]
    if (nextPhoto) {
      result.push(nextPhoto)
    }
  }

  return result
})

watch(
  () => currentPhoto.value,
  (photo) => {
    if (photo) {
      void ensureThumbnailUrl(photo)
      void ensureSlideUrl(photo)
      ensureVisibleThumbnailsLoaded()
    }
  },
  { immediate: true },
)

// Loading state management functions
const startImageLoading = () => {
  isCurrentImageLoading.value = true
  showSpinner.value = false

  // Clear any existing spinner timer
  if (spinnerTimer.value !== null) {
    clearTimeout(spinnerTimer.value)
    spinnerTimer.value = null
  }

  // Show spinner after 500ms delay
  spinnerTimer.value = window.setTimeout(() => {
    if (isCurrentImageLoading.value) {
      showSpinner.value = true
    }
    spinnerTimer.value = null
  }, 500)
}

const finishImageLoading = () => {
  isCurrentImageLoading.value = false
  showSpinner.value = false

  // Clear spinner timer if image loads before 500ms
  if (spinnerTimer.value !== null) {
    clearTimeout(spinnerTimer.value)
    spinnerTimer.value = null
  }
}

// Preload images for smooth navigation
const preloadPhoto = async (photo: Photo) => {
  if (!photo?.clientId) return

  // Skip if already preloaded
  if (preloadedImageCache.value.has(photo.clientId)) return

  const url = (await ensureSlideUrl(photo)) || getResolvedSlideUrl(photo)
  if (!url) return

  const img = new Image()
  img.onload = () => {
    preloadedImageCache.value.set(photo.clientId, img)
  }
  img.onerror = () => {
    logService.warn(LOG_CATEGORIES.FILE, 'Failed to preload photo', {
      photoName: photo.name || 'unknown',
    })
  }
  img.src = url
}

// Preload previous and next photos when current photo changes
const preloadAdjacentPhotos = () => {
  if (previousPhotoData.value) {
    void preloadPhoto(previousPhotoData.value)
  }
  if (nextPhotoData.value) {
    void preloadPhoto(nextPhotoData.value)
  }
}

// Handle main photo load completion to trigger preloading
const handleMainPhotoLoad = () => {
  // Finish loading state for current image
  finishImageLoading()

  // Start preloading adjacent photos after main photo is loaded
  preloadAdjacentPhotos()
}

const handleSlideLoad = (photo: Photo) => {
  if (photo.clientId === currentPhoto.value?.clientId) {
    handleMainPhotoLoad()
  }
}

watch(
  () => slidePhotos.value.map((photo) => photo.clientId).join('|'),
  () => {
    slidePhotos.value.forEach((photo) => {
      void ensureSlideUrl(photo)
    })
    ensureVisibleThumbnailsLoaded()
  },
  { immediate: true },
)

const currentSlidePosition = computed(() => {
  const activeId = currentPhoto.value?.clientId
  if (!activeId) return 0

  const position = slidePhotos.value.findIndex((photo) => photo.clientId === activeId)
  return position >= 0 ? position : 0
})

const slideTrackStyle = computed(() => {
  const slideWidth = viewportWidth.value
  const offsetPerSlide = slideWidth + SLIDE_GAP
  const baseOffset = -currentSlidePosition.value * offsetPerSlide

  return {
    transform: `translate3d(${baseOffset + imageTranslateX.value}px, 0, 0)`,
    gap: `${SLIDE_GAP}px`,
    willChange: 'transform',
  }
})

const slideItemStyle = computed(() => ({
  width: `${viewportWidth.value}px`,
}))

watch(viewportWidth, () => {
  if (imageScale.value > MIN_IMAGE_SCALE) {
    normalizeZoomState()
  }
})

const getActiveImageElement = () => {
  const activeId = currentPhoto.value?.clientId
  if (!activeId) {
    return null
  }

  return slideImageRefs.value.get(activeId) ?? null
}

const clampValue = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value))
}

const getTouchDistance = (touches: TouchList) => {
  if (touches.length < 2) {
    return 0
  }

  const firstTouch = touches[0]
  const secondTouch = touches[1]
  const deltaX = secondTouch.clientX - firstTouch.clientX
  const deltaY = secondTouch.clientY - firstTouch.clientY

  return Math.hypot(deltaX, deltaY)
}

const getTouchCenter = (touches: TouchList) => {
  if (touches.length < 2) {
    return { x: 0, y: 0 }
  }

  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  }
}

const clampImagePan = (panX: number, panY: number, scale = imageScale.value) => {
  if (scale <= MIN_IMAGE_SCALE) {
    return { x: 0, y: 0 }
  }

  const activeImage = getActiveImageElement()
  if (!activeImage) {
    return { x: 0, y: 0 }
  }

  const viewportWidthPx = viewportRef.value?.clientWidth ?? activeImage.clientWidth
  const viewportHeightPx = viewportRef.value?.clientHeight ?? activeImage.clientHeight
  const scaledWidth = activeImage.clientWidth * scale
  const scaledHeight = activeImage.clientHeight * scale
  const maxPanX = Math.max(0, (scaledWidth - viewportWidthPx) / 2)
  const maxPanY = Math.max(0, (scaledHeight - viewportHeightPx) / 2)

  return {
    x: clampValue(panX, -maxPanX, maxPanX),
    y: clampValue(panY, -maxPanY, maxPanY),
  }
}

const applyZoomTransform = (nextScale: number, nextPanX: number, nextPanY: number) => {
  const clampedScale = clampValue(nextScale, MIN_IMAGE_SCALE, MAX_IMAGE_SCALE)
  const clampedPan = clampImagePan(nextPanX, nextPanY, clampedScale)

  imageScale.value = clampedScale
  imagePanX.value = clampedPan.x
  imagePanY.value = clampedPan.y
}

const resetZoomState = () => {
  imageScale.value = MIN_IMAGE_SCALE
  imagePanX.value = 0
  imagePanY.value = 0
  isPinching.value = false
  isZoomPanning.value = false
}

const normalizeZoomState = () => {
  if (imageScale.value <= MIN_IMAGE_SCALE + 0.01) {
    resetZoomState()
    return
  }

  const clampedPan = clampImagePan(imagePanX.value, imagePanY.value)
  imagePanX.value = clampedPan.x
  imagePanY.value = clampedPan.y
}

const getSlideImageStyle = (photo: Photo) => {
  if (photo.clientId !== currentPhoto.value?.clientId) {
    return undefined
  }

  return {
    transform: `translate3d(${imagePanX.value}px, ${imagePanY.value}px, 0) scale(${imageScale.value})`,
    transformOrigin: 'center center',
    transition: isTouch.value ? 'none' : 'transform 180ms ease-out',
    willChange: imageScale.value > MIN_IMAGE_SCALE ? 'transform' : 'auto',
  }
}

const getThumbnailUrl = (photo: Photo): string => {
  if (!loadedThumbnails.value.has(photo.clientId)) {
    return '' // Return empty src until loaded
  }

  if (photo._resolvedOfflineThumbnailUrl) {
    return photo._resolvedOfflineThumbnailUrl
  }

  if (photo._thumbnailData) {
    return photo._thumbnailData
  }

  return photo._thumbnailUrl || ''
}

function ensureVisibleThumbnailsLoaded(): void {
  slidePhotos.value.forEach((photo) => {
    if (loadedThumbnails.value.has(photo.clientId)) return

    void ensureThumbnailUrl(photo).then((resolvedSrc) => {
      if (!resolvedSrc) return
      photo._resolvedOfflineThumbnailUrl = resolvedSrc
      loadedThumbnails.value.add(photo.clientId)
    })
  })
}

const loadThumbnail = async (photoId: string, img: HTMLImageElement) => {
  if (loadedThumbnails.value.has(photoId)) return

  await thumbnailLimiter.execute(async () => {
    const photo = photos.value.find((p) => p.clientId === photoId)
    if (photo) {
      const resolvedSrc = await ensureThumbnailUrl(photo)
      if (!resolvedSrc) return

      img.src = resolvedSrc
      loadedThumbnails.value.add(photoId)
    }
  })
}

const setupThumbnailObserver = () => {
  if (thumbnailObserver.value) {
    thumbnailObserver.value.disconnect()
  }

  thumbnailObserver.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement
          const photoId = img.dataset.photoId
          if (photoId && !loadedThumbnails.value.has(photoId)) {
            loadThumbnail(photoId, img)
          }
        }
      })
    },
    {
      root: thumbnailsContainerRef.value,
      rootMargin: '50px',
      threshold: 0.1,
    },
  )
}

const onThumbnailMount = (el: Element, photoId: string) => {
  if (thumbnailObserver.value && el instanceof HTMLImageElement) {
    el.dataset.photoId = photoId
    thumbnailObserver.value.observe(el)
  } else if (el instanceof HTMLImageElement && !loadedThumbnails.value.has(photoId)) {
    void loadThumbnail(photoId, el)
  }
}

const setThumbnailElementRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (el && el instanceof HTMLElement) {
    thumbnailElementRefs.value.set(index, el)
  } else {
    thumbnailElementRefs.value.delete(index)
  }
}

const setSlideImageRef = (photoId: string, el: Element | ComponentPublicInstance | null) => {
  if (el instanceof HTMLImageElement) {
    slideImageRefs.value.set(photoId, el)
  } else {
    slideImageRefs.value.delete(photoId)
  }
}

const totalPhotos = computed(() => photos.value.length)

const photoCaption = computed(() => {
  if (!currentPhoto.value || !currentPhoto.value.takenAt) return ''

  const date = new Date(currentPhoto.value.takenAt)
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' })
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${weekday}, ${hours}:${minutes}\n${year}-${month}-${day}`
})

const photoFilename = computed(() => {
  return currentPhoto.value?.name || ''
})

const photoUploadedBy = computed(() => {
  return currentPhoto.value?.uploadedByName || ''
})

const syncBrowserUrlToCurrentPhoto = async (replace = false) => {
  if (!currentPhoto.value) return

  const currentWorkspace = workspaceStore.currentWorkspace
  if (!currentWorkspace?.clientId) return

  const routeLocation = {
    name: 'photo-detail' as const,
    params: {
      workspaceId: currentWorkspace.clientId,
      photoId: currentPhoto.value.clientId,
    },
  }

  const target = router.resolve(routeLocation)
  if (router.currentRoute.value.fullPath === target.fullPath) return

  if (replace) {
    await router.replace(routeLocation)
    return
  }

  await router.push(routeLocation)
}

watch(currentPhotoIndex, (newIndex, oldIndex) => {
  nextTick(() => {
    const activeId = currentPhoto.value?.clientId
    if (!activeId) return

    const activeImage = slideImageRefs.value.get(activeId)
    if (activeImage && activeImage.complete && activeImage.naturalWidth > 0) {
      handleMainPhotoLoad()
    }
  })

  // Start loading the new current image
  startImageLoading()

  // Reset gesture state when photo changes
  imageTranslateX.value = 0
  isDragging.value = false
  isTouch.value = false
  resetZoomState()

  scrollActiveThumbnailIntoView()
  triggerAutoShowOverlay()

  const liveRegion = document.getElementById('slideshow-live-region')
  if (liveRegion && currentPhoto.value) {
    liveRegion.textContent = `Showing photo ${currentPhotoIndex.value + 1} of ${totalPhotos.value}: ${currentPhoto.value.name}`
  }

  // Update URL when photo changes (but not on initial load)
  if (oldIndex !== undefined && currentPhoto.value) {
    void syncBrowserUrlToCurrentPhoto()
  }

  // Only preload adjacent photos when navigating between photos (not on initial load)
  if (oldIndex !== undefined) {
    preloadAdjacentPhotos()
  }
})

watch(
  () => props.initialPhotoId,
  (newId) => {
    selectPhotoById(newId)
  },
  { immediate: true },
)

watch(
  () => photos.value.map((photo) => photo.clientId).join('|'),
  () => {
    if (!props.isOpen) return
    selectPhotoById(props.initialPhotoId)
  },
)

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectPhotoById(props.initialPhotoId)
      void syncBrowserUrlToCurrentPhoto()

      showOverlays.value = false
      controlsVisible.value = true
      resetControlsTimer()
      triggerAutoShowOverlay()

      // Ensure all tours have full segment data (elevation, waypoints) loaded so the
      // route data composable can compute km-on-route and elevation profile without
      // requiring the user to click a track on the map first.
      void Promise.all(
        tours.value
          .filter((t) => !t._detailsLoaded)
          .map((t) => workspaceStore.ensureTourDetailLoaded(t.clientId)),
      )

      if (isTauriApp) {
        if (isTauriAppleMobileApp) {
          isFullscreen.value = isImmersiveMobileFullscreen.value
        } else {
          void isNativeFullscreen().then((fullscreen) => {
            isFullscreen.value = fullscreen
          })
        }
      }

      // Start loading the initial photo
      if (currentPhoto.value) {
        startImageLoading()
      }

      nextTick(() => {
        setupThumbnailObserver()
        closeButtonRef.value?.focus()
        scrollActiveThumbnailIntoView()
        updateViewportWidth()

        if (resizeObserver && viewportRef.value) {
          if (observedViewport) {
            resizeObserver.unobserve(observedViewport)
          }

          resizeObserver.observe(viewportRef.value)
          observedViewport = viewportRef.value
        }

        // Don't preload adjacent photos immediately when slideshow opens
        // Let the main photo load first, then preload via the @load event
        ensureVisibleThumbnailsLoaded()
      })
    } else {
      document.getElementById('trigger-element')?.focus()

      if (isTauriApp) {
        if (isTauriAppleMobileApp) {
          isImmersiveMobileFullscreen.value = false
          isFullscreen.value = false
        } else {
          void isNativeFullscreen().then((fullscreen) => {
            isFullscreen.value = fullscreen
          })
        }
      }

      // Clear loading state when closing
      finishImageLoading()
      resetZoomState()
      imageTranslateX.value = 0
      isTouch.value = false
      isDragging.value = false

      if (hideControlsTimer.value !== null) {
        window.clearTimeout(hideControlsTimer.value)
        hideControlsTimer.value = null
      }

      if (resizeObserver && observedViewport) {
        resizeObserver.unobserve(observedViewport)
        observedViewport = null
      }
    }
  },
)

const scrollActiveThumbnailIntoView = () => {
  nextTick(() => {
    const thumbnail = thumbnailElementRefs.value.get(currentPhotoIndex.value)
    if (thumbnail && thumbnailsContainerRef.value) {
      thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  })
}

const navigateToPhoto = (index: number) => {
  currentPhotoIndex.value = index
}

const performSlideTransition = (direction: 1 | -1) => {
  if (isSlideTransitioning.value) return

  const total = photos.value.length
  if (total <= 1) {
    isSlideTransitioning.value = true
    animateToPosition(0, 220, () => {
      imageTranslateX.value = 0
      isSlideTransitioning.value = false
    })
    return
  }

  // Prevent overlapping animations
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
    animationId.value = null
  }

  isDragging.value = false

  const slideOffset = getEffectiveViewportWidth() + SLIDE_GAP
  const targetIndex = currentPhotoIndex.value + direction

  if (targetIndex < 0 || targetIndex >= total) {
    isSlideTransitioning.value = true
    animateToPosition(0, 220, () => {
      imageTranslateX.value = 0
      isSlideTransitioning.value = false
    })
    return
  }

  isSlideTransitioning.value = true

  animateToPosition(direction === 1 ? -slideOffset : slideOffset, 250, () => {
    imageTranslateX.value = 0
    currentPhotoIndex.value = targetIndex
    isSlideTransitioning.value = false
  })
}

const nextPhoto = () => {
  performSlideTransition(1)
}

const prevPhoto = () => {
  performSlideTransition(-1)
}

const downloadCurrentPhoto = () => {
  if (!currentPhoto.value) return

  // Create a link element to trigger the download
  const link = document.createElement('a')

  if (currentPhoto.value.binaryData instanceof File) {
    // Create a URL from the File object for downloading
    const objectUrl = URL.createObjectURL(currentPhoto.value.binaryData)
    link.href = objectUrl
    link.download = currentPhoto.value.name
    link.click()
    // Cleanup next frame(s) instead of fixed timeout
    requestAnimationFrame(() => {
      requestAnimationFrame(() => URL.revokeObjectURL(objectUrl))
    })
    return
  } else if (currentPhoto.value._binaryDataUrl) {
    // Use the data URL for download
    link.href = currentPhoto.value._resolvedOfflineBinaryUrl || currentPhoto.value._binaryDataUrl
    link.download = currentPhoto.value.name
    link.click()
    return
  }

  // If we get here, we couldn't download the file
  showError('Could not download photo: No file available')
}

const shareCurrentPhoto = async () => {
  if (!currentPhoto.value) return

  const currentWorkspace = workspaceStore.currentWorkspace
  if (!currentWorkspace?.clientId) {
    showError('Cannot share photo: No project available')
    return
  }

  // Create the share URL with dedicated photo route
  const route = router.resolve({
    name: 'photo-detail',
    params: {
      workspaceId: currentWorkspace.clientId,
      photoId: currentPhoto.value.clientId,
    },
  })

  const shareUrl = `${window.location.origin}${route.href}`

  try {
    // Check if clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      throw new Error('Clipboard API not available')
    }

    // Copy to clipboard - must be called synchronously from user gesture
    await navigator.clipboard.writeText(shareUrl)
    showSuccess('Photo link copied to clipboard')
  } catch {
    // Fallback for iOS and other browsers with clipboard restrictions
    // Create a temporary textarea element for fallback copying
    const textArea = document.createElement('textarea')
    textArea.value = shareUrl
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (successful) {
      showSuccess('Photo link copied to clipboard')
    } else {
      throw new Error('Copy command failed')
    }
  }
}

const exitFullscreenMode = async (): Promise<void> => {
  if (isTauriAppleMobileApp) {
    isImmersiveMobileFullscreen.value = false
    isFullscreen.value = false
    controlsVisible.value = true
    cursorVisible.value = true
    return
  }

  if (isTauriApp) {
    try {
      if (await isNativeFullscreen()) {
        await setNativeFullscreen(false)
      }
    } catch {
      const { exit } = getBrowserFullscreenApi()
      if (exit && getBrowserFullscreenState()) {
        await exit()
      }
    }
  } else {
    const { exit } = getBrowserFullscreenApi()
    if (exit && getBrowserFullscreenState()) {
      await exit()
    }
  }

  isFullscreen.value = false
}

const closeSlideshow = async () => {
  await exitFullscreenMode()

  // Reset URL to project URL when closing slideshow
  const currentWorkspace = workspaceStore.currentWorkspace
  if (currentWorkspace?.clientId) {
    // Check if we have an active project loaded - if so, navigate to workspace detail
    // This handles cases where we're in slideshow from workspace detail view
    const currentRoute = router.currentRoute.value
    if (currentRoute.params.workspaceId || currentWorkspace.clientId) {
      // Navigate back to workspace detail view
      router.push({
        name: 'workspace-detail',
        params: {
          workspaceId: currentWorkspace.clientId,
        },
      })
    } else {
      // Navigate to workspaces overview
      router.push({
        name: 'workspaces',
      })
    }
  }

  emit('close')
}

const navigateToLocation = () => {
  if (!currentPhoto.value || !currentPhoto.value.hasCoordinates) return

  emit('navigate-to-location', currentPhoto.value.clientId)
  void closeSlideshow()
}

type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

type WebkitFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void
  webkitFullscreenElement?: Element | null
}

const getBrowserFullscreenState = () => {
  const webkitDocument = document as WebkitFullscreenDocument
  return Boolean(document.fullscreenElement || webkitDocument.webkitFullscreenElement)
}

const getBrowserFullscreenApi = () => {
  const element = slideshowRef.value as WebkitFullscreenElement | null
  const webkitDocument = document as WebkitFullscreenDocument

  const request =
    typeof element?.requestFullscreen === 'function'
      ? () => element.requestFullscreen()
      : typeof element?.webkitRequestFullscreen === 'function'
        ? () => element.webkitRequestFullscreen?.()
        : null

  const exit =
    typeof document.exitFullscreen === 'function'
      ? () => document.exitFullscreen()
      : typeof webkitDocument.webkitExitFullscreen === 'function'
        ? () => webkitDocument.webkitExitFullscreen?.()
        : null

  return { request, exit }
}

const toggleBrowserFullscreen = async () => {
  const { request, exit } = getBrowserFullscreenApi()

  if (!request && !exit) {
    if (!isTauriAppleMobileApp) {
      showError('Fullscreen mode is not supported on this browser')
    }
    return
  }

  if (!getBrowserFullscreenState()) {
    try {
      if (request) {
        await request()
        isFullscreen.value = true
      }
    } catch (err) {
      if (err instanceof Error) {
        if (
          err.name === 'TypeError' &&
          err.message.includes('requestFullscreen is not a function')
        ) {
          if (!isTauriAppleMobileApp) {
            showError('Fullscreen mode is not supported on this browser')
          }
        } else if (err.name === 'NotAllowedError') {
          if (!isTauriAppleMobileApp) {
            showError('Fullscreen mode was blocked by browser settings')
          }
        } else {
          if (!isTauriAppleMobileApp) {
            showError('Unable to enter fullscreen mode')
          }
        }
      } else {
        if (!isTauriAppleMobileApp) {
          showError('Unable to enter fullscreen mode')
        }
      }
    }
  } else {
    try {
      if (exit) {
        await exit()
      }
      isFullscreen.value = false
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          showError('Unable to exit fullscreen mode due to browser restrictions')
        } else {
          showError('Unable to exit fullscreen mode')
        }
      } else {
        showError('Unable to exit fullscreen mode')
      }
    }
  }
}

const toggleFullscreen = async () => {
  if (isTauriApp) {
    if (isTauriAppleMobileApp) {
      isImmersiveMobileFullscreen.value = !isImmersiveMobileFullscreen.value
      isFullscreen.value = isImmersiveMobileFullscreen.value
      if (isFullscreen.value) {
        resetControlsTimer()
      } else {
        controlsVisible.value = true
        cursorVisible.value = true
      }
      return
    }

    try {
      const nextFullscreen = !(await isNativeFullscreen())
      await setNativeFullscreen(nextFullscreen)
      isFullscreen.value = nextFullscreen
      return
    } catch {
      const { request, exit } = getBrowserFullscreenApi()
      if (request || exit) {
        await toggleBrowserFullscreen()
        return
      }

      // On Tauri mobile/iPad the slideshow is already effectively fullscreen,
      // so don't show a noisy error when the platform can't toggle a deeper OS mode.
      isFullscreen.value = false
    }
  }

  await toggleBrowserFullscreen()
}

const setAutoShowOverlay = (value: boolean) => {
  autoShowOverlay.value = value
  settingsService.saveSetting(UI_SETTING_KEYS.photoSlideshowAutoShowOverlay, value)
}

const triggerAutoShowOverlay = () => {
  if (!autoShowOverlay.value) return
  showOverlays.value = true
  if (autoHideTimer !== null) clearTimeout(autoHideTimer)
  autoHideTimer = setTimeout(() => {
    showOverlays.value = false
    autoHideTimer = null
  }, 3000)
}

const toggleOverlays = () => {
  showOverlays.value = !showOverlays.value
  // Manually toggling cancels any pending auto-hide
  if (autoHideTimer !== null) {
    clearTimeout(autoHideTimer)
    autoHideTimer = null
  }
}

const handleFullscreenChange = () => {
  if (isTauriApp) {
    if (isTauriAppleMobileApp) {
      isFullscreen.value = isImmersiveMobileFullscreen.value
      return
    }

    void isNativeFullscreen().then((fullscreen) => {
      isFullscreen.value = fullscreen
    })
    return
  }

  isFullscreen.value = getBrowserFullscreenState()
}

let overlaysBeforeFullscreen = false

watch(isFullscreen, (fullscreen) => {
  if (fullscreen) {
    overlaysBeforeFullscreen = showOverlays.value
    if (autoShowOverlay.value) {
      triggerAutoShowOverlay()
    } else {
      showOverlays.value = false
    }
  } else {
    showOverlays.value = overlaysBeforeFullscreen
  }
})

const handleKeyDown = (e: KeyboardEvent) => {
  if (!props.isOpen) return

  if (e.key === 'Escape') {
    void closeSlideshow()
    e.preventDefault()
  } else if (e.key === 'ArrowRight') {
    nextPhoto()
    e.preventDefault()
  } else if (e.key === 'ArrowLeft') {
    prevPhoto()
    e.preventDefault()
  } else if (e.key.toLowerCase() === 'f') {
    toggleFullscreen()
    e.preventDefault()
  } else if (e.key.toLowerCase() === 'i') {
    toggleOverlays()
    e.preventDefault()
  } else if (e.key === 'Tab') {
    const focusableElements = slideshowRef.value?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ) as NodeListOf<HTMLElement>

    if (!focusableElements || focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (e.shiftKey && document.activeElement === firstElement) {
      lastElement.focus()
      e.preventDefault()
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      firstElement.focus()
      e.preventDefault()
    }
  }
}

const hideControls = () => {
  if (hideControlsTimer.value !== null) {
    window.clearTimeout(hideControlsTimer.value)
    hideControlsTimer.value = null
  }

  controlsVisible.value = false
  cursorVisible.value = false

  // Apply cursor hiding style to slideshow element
  if (slideshowRef.value) {
    slideshowRef.value.style.cursor = 'none'
  }
}

const resetControlsTimer = () => {
  controlsVisible.value = true
  cursorVisible.value = true

  if (slideshowRef.value) {
    slideshowRef.value.style.cursor = 'auto'
  }

  if (hideControlsTimer.value !== null) {
    window.clearTimeout(hideControlsTimer.value)
    hideControlsTimer.value = null
  }

  // Only auto-hide controls in fullscreen mode
  if (isFullscreen.value) {
    hideControlsTimer.value = window.setTimeout(() => {
      hideControls()
    }, 1500)
  }
}

const maybeHideControlsForTouchGesture = () => {
  if (isFullscreen.value && !hasHiddenControlsThisGesture.value && controlsVisible.value) {
    hideControls()
    hasHiddenControlsThisGesture.value = true
  }
}

// Touch/swipe gesture handlers
const handleTouchStart = (e: TouchEvent) => {
  const primaryTouch = e.touches[0]
  if (!primaryTouch) return

  isTouch.value = true
  touchStartX.value = primaryTouch.clientX
  touchStartY.value = primaryTouch.clientY
  touchCurrentX.value = primaryTouch.clientX
  touchCurrentY.value = primaryTouch.clientY
  touchStartTime.value = Date.now()
  isDragging.value = false
  hasHiddenControlsThisGesture.value = false

  // Cancel any ongoing animation
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
    animationId.value = null
  }

  isSlideTransitioning.value = false

  if (e.touches.length >= 2) {
    isPinching.value = true
    isZoomPanning.value = false
    pinchStartDistance.value = getTouchDistance(e.touches)
    pinchStartScale.value = imageScale.value
    pinchStartPanX.value = imagePanX.value
    pinchStartPanY.value = imagePanY.value
    const touchCenter = getTouchCenter(e.touches)
    pinchStartCenterX.value = touchCenter.x
    pinchStartCenterY.value = touchCenter.y
    e.preventDefault()
    return
  }

  if (imageScale.value > MIN_IMAGE_SCALE) {
    isPinching.value = false
    isZoomPanning.value = true
    panStartX.value = primaryTouch.clientX
    panStartY.value = primaryTouch.clientY
    panOriginX.value = imagePanX.value
    panOriginY.value = imagePanY.value
    e.preventDefault()
    return
  }

  isPinching.value = false
  isZoomPanning.value = false
  imageTranslateX.value = 0

  // Prevent default to avoid scrolling
  e.preventDefault()
}

const handleTouchMove = (e: TouchEvent) => {
  if (!isTouch.value) return

  if (isPinching.value && e.touches.length >= 2) {
    const touchCenter = getTouchCenter(e.touches)
    const distance = getTouchDistance(e.touches)
    const baseDistance = pinchStartDistance.value || distance || 1
    const nextScale = pinchStartScale.value * (distance / baseDistance)
    const panDeltaX = touchCenter.x - pinchStartCenterX.value
    const panDeltaY = touchCenter.y - pinchStartCenterY.value

    if (Math.abs(nextScale - imageScale.value) > 0.01 || Math.hypot(panDeltaX, panDeltaY) > 4) {
      isDragging.value = true
    }

    maybeHideControlsForTouchGesture()
    applyZoomTransform(
      nextScale,
      pinchStartPanX.value + panDeltaX,
      pinchStartPanY.value + panDeltaY,
    )
    e.preventDefault()
    return
  }

  const primaryTouch = e.touches[0]
  if (!primaryTouch) return

  touchCurrentX.value = primaryTouch.clientX
  touchCurrentY.value = primaryTouch.clientY

  if (isZoomPanning.value || imageScale.value > MIN_IMAGE_SCALE) {
    const deltaX = touchCurrentX.value - panStartX.value
    const deltaY = touchCurrentY.value - panStartY.value

    if (Math.hypot(deltaX, deltaY) > 5) {
      isDragging.value = true
    }

    maybeHideControlsForTouchGesture()
    const clampedPan = clampImagePan(panOriginX.value + deltaX, panOriginY.value + deltaY)
    imagePanX.value = clampedPan.x
    imagePanY.value = clampedPan.y
    e.preventDefault()
    return
  }

  const deltaX = touchCurrentX.value - touchStartX.value

  // Start dragging if moved more than a few pixels
  if (Math.abs(deltaX) > 5) {
    isDragging.value = true
  }

  // Update image position with improved resistance
  if (isDragging.value) {
    maybeHideControlsForTouchGesture()

    let finalDeltaX = deltaX

    // For single photos, add strong resistance to create bounce effect
    if (totalPhotos.value <= 1) {
      const resistance = calculateResistance(deltaX, 150)
      finalDeltaX = deltaX * resistance * 0.3
    } else {
      // Add resistance when at first/last photo with smooth curve
      if (
        (deltaX > 0 && currentPhotoIndex.value === 0) ||
        (deltaX < 0 && currentPhotoIndex.value === totalPhotos.value - 1)
      ) {
        const resistance = calculateResistance(deltaX, 180)
        finalDeltaX = deltaX * resistance * 0.4
      }
    }

    imageTranslateX.value = finalDeltaX
  }

  e.preventDefault()
}

const animateToPosition = (targetX: number, duration = 300, onComplete?: () => void) => {
  const startX = imageTranslateX.value
  const startTime = Date.now()

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)

    // Use ease-out cubic for smooth iOS-like animation
    const easedProgress = 1 - Math.pow(1 - progress, 3)

    imageTranslateX.value = startX + (targetX - startX) * easedProgress

    if (progress < 1) {
      animationId.value = requestAnimationFrame(animate)
    } else {
      animationId.value = null
      imageTranslateX.value = targetX
      onComplete?.()
    }
  }

  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
  }
  animationId.value = requestAnimationFrame(animate)
}

const handleTouchEnd = (e: TouchEvent) => {
  if (!isTouch.value) return

  if (isPinching.value) {
    if (e.touches.length === 1 && imageScale.value > MIN_IMAGE_SCALE) {
      const remainingTouch = e.touches[0]
      isPinching.value = false
      isZoomPanning.value = true
      isDragging.value = false
      touchStartX.value = remainingTouch.clientX
      touchStartY.value = remainingTouch.clientY
      touchCurrentX.value = remainingTouch.clientX
      touchCurrentY.value = remainingTouch.clientY
      panStartX.value = remainingTouch.clientX
      panStartY.value = remainingTouch.clientY
      panOriginX.value = imagePanX.value
      panOriginY.value = imagePanY.value
      e.preventDefault()
      return
    }

    if (e.touches.length === 0) {
      isPinching.value = false
      isZoomPanning.value = false
      isTouch.value = false
      hasHiddenControlsThisGesture.value = false
      normalizeZoomState()
      e.preventDefault()
      return
    }
  }

  if (isZoomPanning.value || imageScale.value > MIN_IMAGE_SCALE) {
    const deltaX = touchCurrentX.value - touchStartX.value
    const deltaY = touchCurrentY.value - touchStartY.value
    const movement = Math.hypot(deltaX, deltaY)

    if (e.touches.length > 0) {
      e.preventDefault()
      return
    }

    if (movement < 10 && !isDragging.value) {
      handleUserActivity()
    }

    isTouch.value = false
    isDragging.value = false
    isZoomPanning.value = false
    hasHiddenControlsThisGesture.value = false
    lastTouchEndTime.value = Date.now()
    normalizeZoomState()
    e.preventDefault()
    return
  }

  const deltaX = touchCurrentX.value - touchStartX.value
  const absDeltaX = Math.abs(deltaX)
  const touchDuration = Date.now() - touchStartTime.value
  const velocity = touchDuration > 0 ? absDeltaX / touchDuration : 0

  // If there was minimal movement, treat it as a tap to show controls
  if (absDeltaX < 10 && !isDragging.value) {
    handleUserActivity() // This will show controls and reset timer
    isTouch.value = false
    isDragging.value = false
    animateToPosition(0, 150)
    e.preventDefault()
    return
  }

  // For single photos, always bounce back (but don't show controls if it was a swipe)
  if (totalPhotos.value <= 1) {
    // Reset swipe state with smooth bounce animation
    isTouch.value = false
    isDragging.value = false
    animateToPosition(0, 400)
    e.preventDefault()
    return
  }

  // Calculate velocity for momentum detection
  const shouldSwitch = absDeltaX > swipeThreshold || velocity > velocityThreshold

  if (shouldSwitch && isDragging.value) {
    if (deltaX > 0) {
      // Swipe right - go to previous photo
      performSlideTransition(-1)
    } else {
      // Swipe left - go to next photo
      performSlideTransition(1)
    }
  } else {
    // Not enough movement/velocity, bounce back
    animateToPosition(0, 300)
  }

  // Reset swipe state
  isTouch.value = false
  isDragging.value = false
  hasHiddenControlsThisGesture.value = false
  lastTouchEndTime.value = Date.now()

  e.preventDefault()
}

const handleUserActivity = (event?: Event) => {
  if (
    event instanceof MouseEvent &&
    event.type === 'mousemove' &&
    (isTouch.value || isDragging.value || Date.now() - lastTouchEndTime.value < 400)
  ) {
    return
  }

  resetControlsTimer()
}

const cleanupBlobUrls = () => {
  blobUrlCache.value.forEach((url) => {
    try {
      URL.revokeObjectURL(url)
    } catch (e) {
      showError('Error revoking blob URL: ' + (e instanceof Error ? e.message : String(e)))
    }
  })
  blobUrlCache.value.clear()
}

const cleanupPreloadedImages = () => {
  preloadedImageCache.value.clear()
}

// Use Vue composable for global event listeners
useEventListener(document, 'keydown', handleKeyDown)
useEventListener(document, 'fullscreenchange', handleFullscreenChange)
useEventListener(document, 'webkitfullscreenchange', handleFullscreenChange as EventListener)

// Set up conditional mousemove listener with Vue reactive approach
let removeMouseMoveListener: (() => void) | null = null
let removeWindowResizeListener: (() => void) | null = null

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen && typeof document !== 'undefined') {
      // Set up mousemove listener when slideshow opens
      document.addEventListener('mousemove', handleUserActivity)
      removeMouseMoveListener = () => {
        document.removeEventListener('mousemove', handleUserActivity)
      }
    } else {
      // Clean up mousemove listener when slideshow closes
      if (removeMouseMoveListener) {
        removeMouseMoveListener()
        removeMouseMoveListener = null
      }
    }
  },
  { immediate: true },
)

onMounted(() => {
  // Initialize controls timer - ensure controls are visible initially
  controlsVisible.value = true
  resetControlsTimer()

  // Setup thumbnail lazy loading observer
  nextTick(() => {
    setupThumbnailObserver()
    updateViewportWidth()

    if (!resizeObserver && typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        updateViewportWidth()
      })

      if (viewportRef.value) {
        resizeObserver.observe(viewportRef.value)
        observedViewport = viewportRef.value
      }
    } else if (!removeWindowResizeListener && typeof window !== 'undefined') {
      const resizeHandler = () => updateViewportWidth()
      window.addEventListener('resize', resizeHandler)
      removeWindowResizeListener = () => {
        window.removeEventListener('resize', resizeHandler)
      }
    }
  })
})

onUnmounted(() => {
  unsubscribeAutoShowOverlaySetting()

  if (autoHideTimer !== null) {
    clearTimeout(autoHideTimer)
    autoHideTimer = null
  }

  // Event listeners are automatically cleaned up by useEventListener composable

  // Clean up conditional mousemove listener
  if (removeMouseMoveListener) {
    removeMouseMoveListener()
    removeMouseMoveListener = null
  }

  if (resizeObserver && observedViewport) {
    resizeObserver.unobserve(observedViewport)
    observedViewport = null
  }

  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }

  if (removeWindowResizeListener) {
    removeWindowResizeListener()
    removeWindowResizeListener = null
  }

  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
    animationId.value = null
  }

  // Clear any existing timer when unmounting
  if (hideControlsTimer.value !== null) {
    window.clearTimeout(hideControlsTimer.value)
    hideControlsTimer.value = null
  }

  isSlideTransitioning.value = false

  // Clear spinner timer
  if (spinnerTimer.value !== null) {
    clearTimeout(spinnerTimer.value)
    spinnerTimer.value = null
  }

  cleanupBlobUrls()
  cleanupPreloadedImages()

  slideImageRefs.value.clear()

  // Cleanup thumbnail observer
  if (thumbnailObserver.value) {
    thumbnailObserver.value.disconnect()
  }
})
</script>

<template>
  <div
    v-if="isOpen"
    ref="slideshowRef"
    class="fixed inset-0 z-110 flex flex-col bg-gray-900"
    role="dialog"
    aria-modal="true"
    aria-labelledby="slideshow-title"
    @click="handleUserActivity"
  >
    <!-- Hidden live region for screen readers -->
    <div id="slideshow-live-region" class="sr-only" aria-live="polite"></div>

    <!-- Screen reader accessible title -->
    <h2 id="slideshow-title" class="sr-only">Photo Slideshow</h2>

    <!-- Control bar at the top -->
    <div
      v-show="controlsVisible"
      :class="[
        // Layout
        'flex-shrink-0',
        'flex',
        'justify-between',
        'items-center',
        // Spacing & positioning
        'p-4',
        'z-110',
      ]"
      :style="controlBarStyle"
    >
      <!-- Left controls group - Close with back arrow -->
      <div class="flex items-center space-x-4 min-w-0">
        <button
          ref="closeButtonRef"
          @click="closeSlideshow"
          :class="[
            // Text & interaction
            'text-white',
            'hover:text-gray-300',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            // Layout
            'flex',
            'items-center',
          ]"
          aria-label="Close slideshow"
        >
          <ArrowLeftIcon :size="24" />
        </button>

        <!-- Photo counter on left for small screens -->
        <div v-if="totalPhotos > 1 && isSmallScreen" class="text-white text-sm font-medium">
          {{ currentPhotoIndex + 1 }} / {{ totalPhotos }}
        </div>
      </div>

      <!-- Center controls - Photo counter (absolutely centered on larger screens) -->
      <div
        v-if="!isSmallScreen"
        :class="[
          // Positioning
          'absolute',
          'left-1/2',
          'transform',
          '-translate-x-1/2',
          // Layout
          'flex',
          'items-center',
          // Text styling
          'text-white',
          'text-center',
        ]"
      >
        <div v-if="totalPhotos > 1" class="text-sm font-medium">
          {{ currentPhotoIndex + 1 }} / {{ totalPhotos }}
        </div>
      </div>

      <!-- Right controls group - Actions and info -->
      <div class="flex items-center space-x-4 min-w-0">
        <button
          v-if="currentPhoto"
          @click="downloadCurrentPhoto"
          :class="[
            // Text & interaction
            'text-white',
            'hover:text-gray-300',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
          ]"
          aria-label="Download current photo"
        >
          <DownloadIcon :size="24" />
        </button>

        <button
          v-if="currentPhoto"
          @click="shareCurrentPhoto"
          :class="[
            // Text & interaction
            'text-white',
            'hover:text-gray-300',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
          ]"
          aria-label="Share current photo"
        >
          <LinkIcon :size="24" />
        </button>

        <button
          v-if="currentPhoto && currentPhoto.hasCoordinates"
          @click="navigateToLocation"
          :class="[
            // Text & interaction
            'text-white',
            'hover:text-gray-300',
            'hover:cursor-pointer',
            // Layout
            'flex',
            'items-center',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
          ]"
          aria-label="View location on map"
        >
          <MapPinIcon :size="24" />
        </button>

        <button
          @click="toggleFullscreen"
          :class="[
            // Text & interaction
            'text-white',
            'hover:text-gray-300',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
          ]"
          :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
        >
          <component :is="isFullscreen ? MinimizeIcon : MaximizeIcon" :size="24" />
        </button>

        <button
          @click="toggleOverlays"
          :class="[
            // Text & interaction
            'text-white',
            'hover:text-gray-300',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
          ]"
          :aria-label="showOverlays ? 'Hide overlays' : 'Show overlays'"
        >
          <InfoIcon :size="24" />
        </button>
      </div>
    </div>

    <!-- Main photo container -->
    <div
      class="flex-1 min-h-0 relative w-full flex items-center justify-center"
      :aria-hidden="currentPhoto === undefined"
    >
      <div v-if="currentPhoto" class="relative w-full h-full flex items-center justify-center">
        <!-- Loading spinner overlay -->
        <div
          v-if="showSpinner"
          class="absolute inset-0 flex items-center justify-center z-20 bg-gray-900 bg-opacity-50"
        >
          <div class="flex flex-col items-center space-y-3">
            <Loader2Icon :size="40" class="text-white animate-spin" />
            <div class="text-white text-sm">Loading...</div>
          </div>
        </div>
        <div
          ref="viewportRef"
          class="relative w-full h-full overflow-hidden"
          :style="{ touchAction: 'none' }"
          @touchstart.passive="false"
          @touchmove.passive="false"
          @touchend.passive="false"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @touchcancel="handleTouchEnd"
        >
          <div class="flex h-full w-full" :style="slideTrackStyle">
            <div
              v-for="photo in slidePhotos"
              :key="photo.clientId"
              class="flex h-full flex-shrink-0 items-center justify-center"
              :style="slideItemStyle"
            >
              <img
                :src="getResolvedSlideUrl(photo)"
                :alt="photo.name || 'Photo'"
                class="max-w-full max-h-full object-contain select-none"
                :style="getSlideImageStyle(photo)"
                :aria-describedby="
                  photo.clientId === currentPhoto?.clientId && currentPhoto.takenAt
                    ? 'photo-info-card'
                    : undefined
                "
                :aria-hidden="photo.clientId !== currentPhoto?.clientId"
                draggable="false"
                :ref="(el) => setSlideImageRef(photo.clientId, el)"
                @load="handleSlideLoad(photo)"
              />
            </div>
          </div>
        </div>

        <!-- Photo Info Overlay (unified card) -->
        <div
          v-if="
            showOverlays &&
            (photoCaption || photoFilename || photoUploadedBy || routeData.kmOnRoute !== null)
          "
          class="absolute text-white text-xs shadow-xl rounded-lg overflow-hidden"
          style="
            min-width: 180px;
            max-width: 220px;
            backdrop-filter: blur(4px);
            background: rgba(0, 0, 0, 0.72);
          "
          :style="photoInfoOverlayStyle"
          id="photo-info-card"
        >
          <!-- Header: date/time + close -->
          <div class="px-3 pt-2 pb-1.5 border-b border-white/10 flex items-start gap-1">
            <div
              v-if="photoCaption"
              class="flex-1 font-semibold text-white/95 leading-tight whitespace-pre-line"
            >
              {{ photoCaption }}
            </div>
            <button
              @click="showOverlays = false"
              class="shrink-0 text-white/40 hover:text-white/80 leading-none text-base mt-0.5 cursor-pointer"
              aria-label="Close info"
            >
              ×
            </button>
          </div>

          <!-- Stats row: km · hm · % -->
          <div
            v-if="
              routeData.kmOnRoute !== null ||
              routeData.gainM !== null ||
              routeData.gradientPct !== null
            "
            class="px-3 pt-1.5 pb-1 flex items-center gap-4"
          >
            <span v-if="routeData.kmOnRoute !== null" class="flex flex-col items-center gap-0.5">
              <span class="text-white/50 text-[9px] uppercase tracking-wide">km</span>
              <span class="font-mono font-semibold text-sm leading-none">{{
                Math.round(routeData.kmOnRoute)
              }}</span>
            </span>
            <span v-if="routeData.gainM !== null" class="flex flex-col items-center gap-0.5">
              <span class="text-white/50 text-[9px] uppercase tracking-wide">↑ hm</span>
              <span class="font-mono font-semibold text-sm leading-none">{{
                Math.round(routeData.gainM)
              }}</span>
            </span>
            <span v-if="routeData.gradientPct !== null" class="flex flex-col items-center gap-0.5">
              <span class="text-white/50 text-[9px] uppercase tracking-wide">grade</span>
              <span class="font-mono font-semibold text-sm leading-none"
                >{{ Math.round(routeData.gradientPct) }}%</span
              >
            </span>
          </div>

          <!-- Elevation sparkline -->
          <div v-if="elevationProfilePath" class="px-3 pt-2 pb-2 border-b border-white/10">
            <div class="relative">
              <svg
                width="100%"
                :height="elevationProfilePath.H"
                :viewBox="`0 0 ${elevationProfilePath.W} ${elevationProfilePath.H}`"
                preserveAspectRatio="none"
                class="opacity-70 block"
              >
                <polyline
                  :points="elevationProfilePath.points"
                  fill="none"
                  stroke="rgba(255,255,255,0.7)"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                  vector-effect="non-scaling-stroke"
                />
              </svg>
              <div
                v-if="elevationProfileMarker"
                class="absolute w-2 h-2 rounded-full bg-orange-500 ring-1 ring-white -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                :style="{
                  left: elevationProfileMarker.x + '%',
                  top: elevationProfileMarker.y + '%',
                }"
              />
            </div>
          </div>

          <!-- Mini map -->
          <div v-if="routeData.routePolyline" class="px-3 py-2 border-b border-white/10">
            <canvas
              ref="miniMapCanvasRef"
              :width="172"
              :height="80"
              class="w-full rounded opacity-90"
              style="display: block"
            />
          </div>

          <!-- Uploaded by + filename (below minimap) -->
          <div v-if="photoUploadedBy || photoFilename" class="px-3 py-1.5 border-b border-white/10">
            <div v-if="photoUploadedBy" class="text-white/50 text-[10px] leading-tight">
              Uploaded by {{ photoUploadedBy }}
            </div>
            <div
              v-if="photoFilename"
              class="text-white/35 text-[10px] mt-0.5 truncate leading-tight"
            >
              {{ photoFilename }}
            </div>
          </div>

          <!-- Auto-show preference -->
          <label class="flex items-center gap-2 px-3 py-2 cursor-pointer select-none">
            <input
              type="checkbox"
              :checked="autoShowOverlay"
              @change="(e) => setAutoShowOverlay((e.target as HTMLInputElement).checked)"
              class="w-3 h-3 accent-orange-500 cursor-pointer"
            />
            <span class="text-white/40 text-[10px] tracking-wide">Show 3s per photo</span>
          </label>
        </div>
      </div>

      <button
        v-if="totalPhotos > 1"
        v-show="controlsVisible"
        @click="prevPhoto"
        :class="[
          // Positioning
          'absolute',
          'left-4',
          // Visual
          'p-2',
          'rounded-full',
          'bg-black',
          'text-white',
          // Interaction
          'hover:cursor-pointer',
          // Focus state
          'focus:outline-none',
          'focus:ring-2',
          'focus:ring-primary-500',
        ]"
        aria-label="Previous photo"
      >
        <ChevronLeftIcon :size="32" />
      </button>

      <button
        v-if="totalPhotos > 1"
        v-show="controlsVisible"
        @click="nextPhoto"
        :class="[
          // Positioning
          'absolute',
          'right-4',
          // Visual
          'p-2',
          'rounded-full',
          'bg-black',
          'text-white',
          // Interaction
          'hover:cursor-pointer',
          // Focus state
          'focus:outline-none',
          'focus:ring-2',
          'focus:ring-primary-500',
        ]"
        aria-label="Next photo"
      >
        <ChevronRightIcon :size="32" />
      </button>
    </div>

    <!-- Thumbnail navigation bar -->
    <div
      v-if="totalPhotos > 1"
      v-show="controlsVisible"
      class="flex-shrink-0 h-24 flex items-center justify-center"
      :style="thumbnailBarStyle"
      role="region"
      aria-label="Photo thumbnails"
    >
      <div
        ref="thumbnailsContainerRef"
        :class="[
          // Layout
          'flex',
          'items-center',
          'h-full',
          // Scrolling
          'overflow-x-auto',
          'max-w-full',
          // Spacing
          'px-4',
          'py-4',
          'space-x-2',
          // Scrollbar styling
          'scrollbar-thin',
          'scrollbar-thumb-gray-600',
        ]"
        role="listbox"
        aria-label="Available photos"
      >
        <div
          v-for="(photo, index) in photos"
          :key="photo.clientId"
          :class="[
            // Layout
            'flex-shrink-0',
            'relative',
            'h-16',
            'aspect-square',
            // Interaction
            'cursor-pointer',
            // Animation
            'transition-all',
            'duration-200',
            'hover:scale-105',
            // Custom class
            'thumbnail',
            `thumbnail-${index}`,
          ]"
          :ref="(el) => setThumbnailElementRef(el, index)"
          @click="navigateToPhoto(index)"
          tabindex="0"
          :aria-selected="index === currentPhotoIndex"
          :aria-label="`Photo ${index + 1} of ${totalPhotos}: ${photo.name || 'Untitled Photo'}`"
          role="option"
          @keydown.enter="navigateToPhoto(index)"
          @keydown.space.prevent="navigateToPhoto(index)"
        >
          <!-- Placeholder that shows when thumbnail is not loaded -->
          <div
            v-if="!loadedThumbnails.has(photo.clientId)"
            class="h-full w-full rounded bg-gray-50"
            :class="{
              'opacity-25': index !== currentPhotoIndex,
              'ring-2 ring-gray-400': index === currentPhotoIndex,
            }"
          ></div>

          <!-- Actual thumbnail image - always rendered for intersection observer -->
          <img
            :src="getThumbnailUrl(photo)"
            :alt="`Thumbnail for ${photo.name || 'Untitled Photo'}`"
            class="h-full w-full object-cover rounded"
            :class="{
              'opacity-50': index !== currentPhotoIndex && loadedThumbnails.has(photo.clientId),
              'opacity-0': !loadedThumbnails.has(photo.clientId),
              'ring-2 ring-gray-400':
                index === currentPhotoIndex && loadedThumbnails.has(photo.clientId),
            }"
            :ref="(el) => onThumbnailMount(el as Element, photo.clientId)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
